import {useApi} from "../../../hooks/UseApi";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {DataSourceDocumentListResponse, DocumentDataResponse} from "../../../models/collections.models";
import {DataSourceEditPermissionResult} from "../../../models/models";
import {useNavigate, useParams} from "react-router-dom";
import axios from "axios";
import {useSnackbar} from "../../../contexts/SnackbarContext";
import {useTranslation} from "react-i18next";
import {collectionService} from "../../../services/collection.service";
import useDebouncedValue from "../../../hooks/useDebouncedValue";

export const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

interface UseDataSourceDocumentsListType {
    hasEditPermission: boolean;
    documents: DocumentDataResponse[];
    dataSourceName: string;
    documentsDataSourceId: string;
    loading: boolean;

    searchTerm: string;
    currentPage: number;
    totalPages: number;
    pageSize: number;
    pageSizeOptions: number[];

    contextMenu: {
        mouseX?: number;
        mouseY?: number;
        anchorEl?: HTMLElement | null;
        document: DocumentDataResponse;
    } | null;

    addDocumentDialog: boolean;
    setAddDocumentDialog: (open: boolean) => void;
    documentToDelete: DocumentDataResponse | null;
    isDeleteDialogOpen: boolean;
    setIsDeleteDialogOpen: (open: boolean) => void;

    confirmDeleteDocument: () => Promise<void>;
    handleDocumentAdded: () => Promise<void>;
    handleContextMenu: (event: React.MouseEvent, document: DocumentDataResponse) => void;
    handleCloseContextMenu: () => void;
    handleMenuPreview: (doc: DocumentDataResponse) => void;
    handleMenuDownload: (doc: DocumentDataResponse) => Promise<void>;
    handleMenuDelete: (doc: DocumentDataResponse) => void;
    handleBackToSource: () => void;

    handleSearchTermChange: (newTerm: string) => void;
    handlePageChange: (_: React.ChangeEvent<unknown>, newPage: number) => void;
    handlePageSizeChange: (newSize: number) => void;
}


export const useDataSourceDocumentsList = (): UseDataSourceDocumentsListType => {
    const {id} = useParams<{ id: string }>();

    const navigate = useNavigate();
    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();
    const {downloadDocumentForId} = useApi();

    const [hasEditPermission, setHasEditPermission] = useState<DataSourceEditPermissionResult>(false);

    const [documentsDataSourceId, setDocumentsDataSourceId] = useState<string>("");
    const [documents, setDocuments] = useState<DocumentDataResponse[]>([]);
    const [dataSourceName, setDataSourceName] = useState<string>("");

    const [loading, setLoading] = useState<boolean>(true);
    const [addDocumentDialog, setAddDocumentDialog] = useState<boolean>(false);

    const [documentToDelete, setDocumentToDelete] = useState<DocumentDataResponse | null>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);

    const [currentPage, setCurrentPage] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);

    const [searchTerm, setSearchTerm] = useState<string>("");
    const debouncedSearch = useDebouncedValue(searchTerm, 300);
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchData = useCallback(async () => {
        const applyFetchedData = (data: DataSourceDocumentListResponse) => {
            setDocuments(data.content);
            setTotalPages(data.totalPages);
            setPageSize(data.pageable.pageSize);
            setCurrentPage(data.pageable.pageNumber);
        };

        if (!id) {
            console.error("ID not found");
            return;
        }

        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setDocuments([]);
        setLoading(true);

        try {
            const [dataSourceResult, docsResult, editPermissionResult] = await Promise.allSettled([
                collectionService.fetchCollection(id),
                axios.get<DataSourceDocumentListResponse>(`/documents/by-documents-collection/${id}`, {
                    params: {page: currentPage, size: pageSize, name: debouncedSearch},
                    signal: controller.signal
                }),
                axios.get<DataSourceEditPermissionResult>(`/collections/${id}/documents/add`),
            ]);

            if (dataSourceResult.status === "fulfilled") {
                const dataSourceData = dataSourceResult.value.data;
                setDocumentsDataSourceId(dataSourceData.id.toString());
                setDataSourceName(dataSourceData.name);
            } else {
                console.error("Error fetching data source:", dataSourceResult.reason);
            }

            if (docsResult.status === "fulfilled") {
                applyFetchedData(docsResult.value.data);
            } else {
                console.error("Documents fetch failed:", docsResult.reason);
            }

            if (editPermissionResult.status === "fulfilled") {
                setHasEditPermission(editPermissionResult.value.data);
            } else {
                console.error("Edit permission fetch failed:", editPermissionResult.reason);
            }
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error("Unexpected error:", error);
            }
        } finally {
            setLoading(false);
        }
    }, [id, currentPage, pageSize, debouncedSearch]);

    const confirmDeleteDocument = async () => {
        if (!documentToDelete) return;

        try {
            await collectionService.deleteDocument(documentToDelete.id);
            showSnackbar(t('dataSourceDocuments.deleteSuccess'), "success");
            await fetchData();
        } catch (error) {
            console.error("Error deleting document:", error);
            showSnackbar(t('dataSourceDocuments.deleteError'), "error");
        } finally {
            setIsDeleteDialogOpen(false);
            setDocumentToDelete(null);
        }
    };

    useEffect(() => {
        if (id) {
            void fetchData();
        } else {
            navigate("/data-sources/list");
        }

        return () => {
            abortControllerRef.current?.abort();
        };
    }, [id, fetchData]);

    const [contextMenu, setContextMenu] = useState<{
        mouseX?: number;
        mouseY?: number;
        anchorEl?: HTMLElement | null;
        document: DocumentDataResponse;
    } | null>(null);

    const handleContextMenu = useCallback((event: React.MouseEvent, document: DocumentDataResponse) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
            anchorEl: event.currentTarget as HTMLElement,
            document,
        });
    }, []);

    const handleCloseContextMenu = useCallback(() => {
        setContextMenu(null);
    }, []);

    const handleMenuPreview = useCallback((doc: DocumentDataResponse) => {
        handleCloseContextMenu();
        navigate(`/documents/view/${doc.id}`);
    }, [handleCloseContextMenu, navigate]);

    const handleMenuDownload = useCallback(async (doc: DocumentDataResponse) => {
        await downloadDocumentForId(doc.id, doc.name);
        handleCloseContextMenu();
    }, [downloadDocumentForId, handleCloseContextMenu]);

    const handleMenuAiAssistant = useCallback((doc: DocumentDataResponse) => {
        handleCloseContextMenu();
        if (doc.conversationId) {
            navigate(`/chat/${doc.conversationId}`);
        }
    }, [handleCloseContextMenu, navigate]);

    const handleMenuDelete = useCallback((doc: DocumentDataResponse) => {
        if (!hasEditPermission) return;

        setDocumentToDelete(doc);
        setIsDeleteDialogOpen(true);
        handleCloseContextMenu();
    }, [handleCloseContextMenu, hasEditPermission]);

    const handleBackToSource = () => {
        navigate(-1);
    };

    const handleDocumentAdded = async () => {
        await fetchData();
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
        // sessionStorage.setItem(ADMIN_DATA_SOURCE_CURRENT_PAGE, value.toString());
        setCurrentPage(newPage - 1);
    };

    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize);
        setCurrentPage(0);
    };

    const handleSearchTermChange = (newTerm: string) => {
        setSearchTerm(newTerm);
        setCurrentPage(0);
    };

    return {
        hasEditPermission,
        documents,
        dataSourceName,
        documentsDataSourceId,
        loading,

        searchTerm,
        currentPage,
        totalPages,
        pageSize,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        contextMenu,

        addDocumentDialog,
        setAddDocumentDialog,
        documentToDelete,
        isDeleteDialogOpen,
        setIsDeleteDialogOpen,

        confirmDeleteDocument,
        handleDocumentAdded,
        handleContextMenu,
        handleCloseContextMenu,
        handleMenuPreview,
        handleMenuDownload,
        handleMenuDelete,
        handleBackToSource,

        handleSearchTermChange,
        handlePageChange,
        handlePageSizeChange,
    };
};